import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ArrowRight, CheckCircle, Lock, EnvelopeSimple, Key, MagnifyingGlass, X, Buildings, CircleNotch } from "@phosphor-icons/react";
import { toast } from "sonner";
import BrandLogo from "@/components/layout/BrandLogo";

export default function SignIn() {
  const { user, login, completePasswordChange, verifyMfa, resendMfa } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("signin"); // signin | register
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [regPassword, setRegPassword] = useState("");
  const [regConfirmPassword, setRegConfirmPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [signInDisclaimer, setSignInDisclaimer] = useState(null);

  // MFA (email OTP second factor) — challenge shown after primary auth succeeds
  const [mfaTicket, setMfaTicket] = useState("");
  const [mfaEmail, setMfaEmail] = useState("");
  const [mfaCode, setMfaCode] = useState("");
  const [mfaBusy, setMfaBusy] = useState(false);
  const [mfaResendCooldown, setMfaResendCooldown] = useState(0);

  // Forgot-password modal state
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotStep, setForgotStep] = useState("request"); // request | reset | done
  const [forgotOtp, setForgotOtp] = useState("");
  const [forgotNewPass, setForgotNewPass] = useState("");
  const [forgotConfirmPass, setForgotConfirmPass] = useState("");
  const [forgotBusy, setForgotBusy] = useState(false);

  // First-login "must change password" screen state
  const [mustChange, setMustChange] = useState(null); // {user}
  const [mcCurrent, setMcCurrent] = useState("");  // temp password auto-passed from previous step
  const [mcNew, setMcNew] = useState("");
  const [mcConfirm, setMcConfirm] = useState("");
  const [mcOtp, setMcOtp] = useState("");
  const [mcOtpSent, setMcOtpSent] = useState(false);
  const [mcBusy, setMcBusy] = useState(false);

  // OTP flow — one variable serves both registration and firm-request forms
  const [otpStep, setOtpStep] = useState("form"); // form | verify | done
  const [otpEmail, setOtpEmail] = useState("");
  const [otpPurpose, setOtpPurpose] = useState("register"); // register | firm_request
  const [otpCode, setOtpCode] = useState("");
  const [otpBusy, setOtpBusy] = useState(false);

  // Registration state
  const [regName, setRegName] = useState("");
  const [regEmail, setRegEmail] = useState("");
  const [regFirmId, setRegFirmId] = useState("");
  const [regFirmName, setRegFirmName] = useState("");
  const [regInterests, setRegInterests] = useState([]);
  const [regFcaNumber, setRegFcaNumber] = useState("");
  const [industries, setIndustries] = useState([]);

  useEffect(() => {
    if (user) nav("/");
    api.get("/disclaimers/sign_in").then((r) => setSignInDisclaimer(r.data)).catch(() => setSignInDisclaimer(null));
  }, [user, nav]);

  useEffect(() => {
    if (tab !== "register") return;
    // Fetch admin-curated industries (source of truth for interests)
    api.get("/companies/filters/options")
      .then((r) => setIndustries(Array.isArray(r.data?.industries) ? r.data.industries : []))
      .catch(() => setIndustries([]));
  }, [tab]);

  const handleSignIn = async (e, override) => {
    e?.preventDefault();
    const em = override || email;
    if (!em) return;
    setBusy(true);
    try {
      const res = await login(em, password || undefined);
      if (res?.mfaRequired) {
        setMfaTicket(res.mfaTicket);
        setMfaEmail(res.email);
        setMfaCode("");
        setMfaResendCooldown(30);
        toast.success("We emailed you a 6-digit sign-in code.");
        return;
      }
      if (res?.mustChangePassword) {
        setMustChange({ user: res.user });
        setMcCurrent(password);
        toast.info("You must set a new password before continuing.");
        return;
      }
      toast.success("Welcome back");
      nav("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sign-in failed");
    } finally {
      setBusy(false);
    }
  };

  const submitMfa = async (e) => {
    e?.preventDefault();
    if (mfaCode.length !== 6) return;
    setMfaBusy(true);
    try {
      await verifyMfa(mfaTicket, mfaCode);
      toast.success("Welcome back");
      nav("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Verification failed");
    } finally {
      setMfaBusy(false);
    }
  };

  const handleResendMfa = async () => {
    if (mfaResendCooldown > 0 || !mfaTicket) return;
    try {
      await resendMfa(mfaTicket);
      setMfaResendCooldown(30);
      toast.success("A new code has been sent.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not resend code");
    }
  };

  const cancelMfa = () => {
    setMfaTicket("");
    setMfaEmail("");
    setMfaCode("");
    setPassword("");
  };

  useEffect(() => {
    if (mfaResendCooldown <= 0) return undefined;
    const t = setTimeout(() => setMfaResendCooldown((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [mfaResendCooldown]);

  const requestForgotOtp = async () => {
    if (!forgotEmail) { toast.error("Enter your email"); return; }
    setForgotBusy(true);
    try {
      await api.post("/auth/forgot-password", { email: forgotEmail });
      toast.success("If that email is on file, a reset code has been sent.");
      setForgotStep("reset");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Request failed");
    } finally { setForgotBusy(false); }
  };

  const submitReset = async () => {
    if (forgotNewPass !== forgotConfirmPass) { toast.error("Passwords do not match"); return; }
    setForgotBusy(true);
    try {
      await api.post("/auth/reset-password", {
        email: forgotEmail, otp: forgotOtp,
        new_password: forgotNewPass, confirm_password: forgotConfirmPass,
      });
      toast.success("Password reset. You can now sign in with your new password.");
      setForgotOpen(false);
      setForgotStep("request");
      setForgotEmail(""); setForgotOtp(""); setForgotNewPass(""); setForgotConfirmPass("");
      setEmail(forgotEmail);
      setPassword(forgotNewPass);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed");
    } finally { setForgotBusy(false); }
  };

  const requestMcOtp = async () => {
    setMcBusy(true);
    try {
      await api.post("/me/change-password/request-otp");
      setMcOtpSent(true);
      toast.success("Verification code sent to your email.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send code");
    } finally { setMcBusy(false); }
  };

  const submitMustChange = async () => {
    if (mcNew !== mcConfirm) { toast.error("Passwords do not match"); return; }
    if (!mcOtp) { toast.error("Enter the verification code"); return; }
    setMcBusy(true);
    try {
      const r = await api.post("/me/change-password", {
        current_password: mcCurrent, new_password: mcNew,
        confirm_password: mcConfirm, otp: mcOtp,
      });
      completePasswordChange(r.data.token, r.data.user);
      toast.success("Password set. Welcome!");
      nav("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Password change failed");
    } finally { setMcBusy(false); }
  };

  const handleRegister = async (e) => {
    e?.preventDefault();
    if (!regName.trim() || !regEmail.trim()) {
      toast.error("Please fill in every field.");
      return;
    }
    // Firm: either a picked firm (regFirmId) OR a free-text firm name (regFirmName)
    if (!regFirmId && !regFirmName.trim()) {
      toast.error("Please enter your firm name.");
      return;
    }
    if (!regPassword || regPassword !== regConfirmPassword) {
      toast.error("Passwords must match.");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/auth/request-code", {
        email: regEmail.trim(),
        purpose: "register",
        payload: {
          name: regName.trim(),
          // Server accepts EITHER firm_id (picked from typeahead) OR firm_name (free text).
          firm_id: regFirmId || null,
          firm_name: regFirmId ? null : regFirmName.trim(),
          password: regPassword,
          confirm_password: regConfirmPassword,
          interested_industries: regInterests,
          fca_number: regFcaNumber.trim() || null,
        },
      });
      setOtpEmail(regEmail.trim().toLowerCase());
      setOtpPurpose("register");
      // MFA kill-switch: when the server returns `verified:true` it has
      // already created the account (no code was ever sent). Jump straight
      // to the confirmation state instead of showing an OTP input.
      if (r?.data?.verified) {
        setOtpStep("done");
        toast.success("Your application has been submitted.");
      } else {
        setOtpStep("verify");
        toast.success("We've emailed a 6-digit code. Please check your inbox.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit application");
    } finally {
      setBusy(false);
    }
  };

  const submitOtp = async (e) => {
    e?.preventDefault();
    if (!otpCode.trim()) return;
    setOtpBusy(true);
    try {
      await api.post("/auth/verify-code", { email: otpEmail, purpose: otpPurpose, code: otpCode.trim() });
      setOtpStep("done");
      setOtpCode("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Verification failed");
    } finally { setOtpBusy(false); }
  };

  const resendOtp = async () => {
    try {
      const payload = {
        name: regName.trim(),
        firm_id: regFirmId || null,
        firm_name: regFirmId ? null : regFirmName.trim(),
        password: regPassword,
        confirm_password: regConfirmPassword,
        interested_industries: regInterests,
        fca_number: regFcaNumber.trim() || null,
      };
      await api.post("/auth/request-code", { email: otpEmail, purpose: "register", payload });
      toast.success("New code sent");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not resend");
    }
  };

  return (
    <div className="min-h-screen flex bg-background text-foreground">
      <div className="hidden lg:flex flex-1 relative overflow-hidden bg-slate-900 text-white">
        <div className="absolute inset-0 grid-backdrop opacity-40" />
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <div className="flex items-center gap-4">
            <BrandLogo className="h-16 w-auto object-contain" forceLight />
            <div className="leading-tight border-l border-white/15 pl-4">
              <div className="font-display font-bold text-2xl">Inventya Capital</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/60 mt-1 font-semibold">Investor Portal</div>
            </div>
          </div>
          <div className="max-w-md">
            <div className="text-[11px] uppercase tracking-[0.2em] text-emerald-400 mb-4 font-bold">Investor Access</div>
            <h1 className="font-display text-4xl lg:text-6xl font-bold leading-[1.02] tracking-tight">
              A private deal room for
              <span className="text-emerald-400"> investment-grade companies.</span>
            </h1>
            <p className="mt-6 text-white/70 leading-relaxed text-lg">
              Curated raises, audited financials, and a secure data room — every page watermarked and every view logged for compliance.
            </p>
          </div>
          <div className="text-xs text-white/40">© 2026 Inventya Capital. Access restricted to qualified investors.</div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <BrandLogo className="h-16 w-auto object-contain" />
            <div><div className="font-display font-bold text-xl">Inventya Capital</div></div>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 p-1 rounded-lg bg-secondary/60 mb-6">
            <button
              type="button"
              onClick={() => { setTab("signin"); setOtpStep("form"); }}
              data-testid="tab-signin"
              className={`flex-1 py-2 rounded-md text-sm font-medium transition-colors ${tab === "signin" ? "bg-background shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
            >Sign in</button>
            <button
              type="button"
              onClick={() => { setTab("register"); setOtpStep("form"); }}
              data-testid="tab-register"
              className={`flex-1 py-2 rounded-md text-sm font-medium transition-colors ${tab === "register" ? "bg-background shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
            >Create account</button>
          </div>

          {tab === "signin" ? (
            mfaTicket ? (
              <div data-testid="mfa-challenge">
                <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-3 font-bold">Two-factor verification</div>
                <h2 className="font-display text-4xl font-bold tracking-tight">Enter your sign-in code</h2>
                <p className="mt-3 text-muted-foreground text-sm leading-relaxed">
                  For security, every sign-in requires a 6-digit code. We&apos;ve sent one to <b>{mfaEmail}</b>. It expires in 10 minutes.
                </p>
                <form onSubmit={submitMfa} className="mt-8 space-y-4">
                  <Input
                    value={mfaCode}
                    onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    className="h-14 text-center font-mono text-2xl tracking-[0.5em] num-display"
                    maxLength={6}
                    autoFocus
                    data-testid="mfa-code-input"
                    placeholder="••••••"
                    inputMode="numeric"
                  />
                  <Button
                    type="submit"
                    disabled={mfaBusy || mfaCode.length !== 6}
                    data-testid="mfa-verify-btn"
                    className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-white"
                  >
                    {mfaBusy ? "Verifying…" : "Verify & sign in"}
                    <ArrowRight size={16} className="ml-2" />
                  </Button>
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <button type="button" onClick={cancelMfa} className="hover:text-foreground" data-testid="mfa-cancel-btn">← Use a different account</button>
                    <button
                      type="button"
                      onClick={handleResendMfa}
                      disabled={mfaResendCooldown > 0}
                      data-testid="mfa-resend-btn"
                      className="text-emerald-700 dark:text-emerald-400 hover:underline disabled:opacity-50 disabled:no-underline"
                    >
                      {mfaResendCooldown > 0 ? `Resend in ${mfaResendCooldown}s` : "Didn't get it? Resend →"}
                    </button>
                  </div>
                </form>
              </div>
            ) : (
            <>
              <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-3 font-bold">Sign in</div>
              <h2 className="font-display text-4xl font-bold tracking-tight">Welcome back</h2>
              <p className="mt-3 text-muted-foreground">Enter your registered email to access the portal.</p>

              <form onSubmit={handleSignIn} className="mt-8 space-y-4">
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5 block">Email</Label>
                  <Input
                    type="email"
                    required
                    data-testid="signin-email-input"
                    placeholder="you@fund.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="h-11"
                  />
                </div>
                <div>
                  <div className="flex items-baseline justify-between mb-1.5">
                    <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Password</Label>
                    <button
                      type="button"
                      onClick={() => { setForgotEmail(email); setForgotOpen(true); setForgotStep("request"); }}
                      className="text-[11px] text-emerald-700 dark:text-emerald-400 hover:underline font-semibold"
                      data-testid="forgot-password-link"
                    >
                      Forgot password?
                    </button>
                  </div>
                  <Input
                    type="password"
                    data-testid="signin-password-input"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="h-11"
                    autoComplete="current-password"
                  />
                </div>
                <Button type="submit" disabled={busy} data-testid="signin-submit-btn" className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-white">
                  {busy ? "Signing in…" : "Sign in"}
                  <ArrowRight size={16} className="ml-2" />
                </Button>
              </form>

              <div className="mt-10">
                {signInDisclaimer && (
                  <div className="rounded-xl border border-amber-200 dark:border-amber-900/60 bg-amber-50/60 dark:bg-amber-950/20 p-4" data-testid="signin-fca-disclaimer">
                    <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-amber-800 dark:text-amber-300 mb-2">
                      FCA Notice
                    </div>
                    <div className="text-xs text-amber-900/90 dark:text-amber-100/90 leading-relaxed whitespace-pre-line">
                      {signInDisclaimer.body}
                    </div>
                  </div>
                )}
              </div>
            </>
            )
          ) : otpStep === "verify" ? (
            <>
              <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-3 font-bold">Verify email</div>
              <h2 className="font-display text-4xl font-bold tracking-tight">Check your inbox</h2>
              <p className="mt-3 text-muted-foreground text-sm leading-relaxed">
                We&apos;ve sent a 6-digit code to <b>{otpEmail}</b>. Enter it below to confirm your email address.
              </p>
              <form onSubmit={submitOtp} className="mt-8 space-y-4">
                <Input
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value.replace(/\D/g,"").slice(0,6))}
                  className="h-14 text-center font-mono text-2xl tracking-[0.5em] num-display"
                  maxLength={6}
                  autoFocus
                  data-testid="otp-code-input"
                  placeholder="••••••"
                />
                <Button type="submit" disabled={otpBusy || otpCode.length !== 6} data-testid="otp-verify-btn" className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-white">
                  {otpBusy ? "Verifying…" : "Verify email"}
                </Button>
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <button type="button" onClick={() => { setOtpStep("form"); setOtpCode(""); }} className="hover:text-foreground">← Back</button>
                  <button type="button" onClick={resendOtp} className="text-emerald-700 dark:text-emerald-400 hover:underline">Didn&apos;t get it? Resend →</button>
                </div>
              </form>
            </>
          ) : otpStep === "done" ? (
            <>
              <div className="mx-auto h-12 w-12 rounded-full bg-emerald-100 dark:bg-emerald-900/40 grid place-items-center mb-4">
                <CheckCircle size={26} weight="fill" className="text-emerald-600" />
              </div>
              <h2 className="font-display text-3xl font-bold tracking-tight text-center">
                Application submitted
              </h2>
              <p className="mt-3 text-muted-foreground text-sm leading-relaxed text-center max-w-sm mx-auto">
                Your investor application is with our compliance team for classification review. You&apos;ll receive an email once approved.
              </p>
              <div className="mt-6 text-center">
                <Button variant="outline" onClick={() => { setOtpStep("form"); setTab("signin"); }} data-testid="otp-done-back-btn">Back to sign in</Button>
              </div>
            </>
          ) : (
            <>
              <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-3 font-bold">Register</div>
              <h2 className="font-display text-4xl font-bold tracking-tight">Create your investor account</h2>
              <p className="mt-3 text-muted-foreground">Sign up using your work email — we verify against your firm&apos;s approved domain.</p>

              <form onSubmit={handleRegister} className="mt-8 space-y-4">
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5 block">Full name</Label>
                  <Input value={regName} onChange={(e) => setRegName(e.target.value)} className="h-11" data-testid="register-name-input" required />
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5 block">Work email</Label>
                  <Input type="email" value={regEmail} onChange={(e) => setRegEmail(e.target.value)} className="h-11" placeholder="you@yourfirm.com" data-testid="register-email-input" required />
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5 block">Your investment firm</Label>
                  <FirmTypeahead
                    value={{ id: regFirmId, name: regFirmName }}
                    onSelect={(f) => { setRegFirmId(f?.id || ""); setRegFirmName(f?.name || ""); }}
                    onFreeText={(name) => { setRegFirmId(""); setRegFirmName(name); }}
                  />
                  <div className="mt-2 text-[11px] text-muted-foreground">
                    Pick from the list if your firm is already onboarded — otherwise just type your firm name and our compliance team will review.
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5 block">
                    Interested industries <span className="text-muted-foreground/70 normal-case tracking-normal">(optional — get email alerts when a new company is added)</span>
                  </Label>
                  <IndustriesPicker options={industries} value={regInterests} onChange={setRegInterests} />
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5 block">
                    FCA Registration Number <span className="text-muted-foreground/70 normal-case tracking-normal">(optional)</span>
                  </Label>
                  <Input
                    value={regFcaNumber}
                    onChange={(e) => setRegFcaNumber(e.target.value)}
                    className="h-11"
                    placeholder="e.g. 123456"
                    maxLength={60}
                    data-testid="register-fca-input"
                  />
                  <div className="mt-1 text-[11px] text-muted-foreground">If your firm is FCA-authorised, providing your reference number accelerates our compliance review.</div>
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5 block">Choose a password</Label>
                  <Input type="password" value={regPassword} onChange={(e) => setRegPassword(e.target.value)} className="h-11" data-testid="register-password-input" required />
                  <div className="text-[11px] text-muted-foreground mt-1">Min 8 chars, with upper, lower, number and symbol.</div>
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5 block">Confirm password</Label>
                  <Input type="password" value={regConfirmPassword} onChange={(e) => setRegConfirmPassword(e.target.value)} className="h-11" data-testid="register-confirm-password-input" required />
                </div>
                <Button type="submit" disabled={busy || (!regFirmId && !regFirmName.trim())} data-testid="register-submit-btn" className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-white">
                  {busy ? "Submitting…" : "Submit application"}
                  <ArrowRight size={16} className="ml-2" />
                </Button>
                <p className="text-[11px] text-center text-muted-foreground">Your firm and email domain are reviewed by our compliance team. You&apos;ll receive an email once your account is approved.</p>
              </form>
            </>
          )}
        </div>
      </div>

      {/* Forgot Password modal */}
      <Dialog open={forgotOpen} onOpenChange={(o) => { setForgotOpen(o); if (!o) { setForgotStep("request"); setForgotOtp(""); setForgotNewPass(""); setForgotConfirmPass(""); } }}>
        <DialogContent className="max-w-md" data-testid="forgot-password-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-bold flex items-center gap-2">
              <Key size={20} className="text-emerald-600" weight="fill" /> Reset your password
            </DialogTitle>
          </DialogHeader>
          {forgotStep === "request" ? (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">Enter your email and we&apos;ll send you a 6-digit reset code.</p>
              <div>
                <Label className="text-xs uppercase tracking-wider font-semibold">Email</Label>
                <Input type="email" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} className="mt-1 h-11" placeholder="you@fund.com" data-testid="forgot-email-input" />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setForgotOpen(false)}>Cancel</Button>
                <Button onClick={requestForgotOtp} disabled={forgotBusy} data-testid="forgot-send-btn" className="bg-slate-900 text-white hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-900">
                  {forgotBusy ? "Sending…" : "Send code"}
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">Enter the 6-digit code we sent to <b>{forgotEmail}</b> and choose a new password.</p>
              <div>
                <Label className="text-xs uppercase tracking-wider font-semibold">6-digit code</Label>
                <Input value={forgotOtp} onChange={(e) => setForgotOtp(e.target.value)} maxLength={6} className="mt-1 h-11 font-mono tracking-widest text-lg" data-testid="forgot-otp-input" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider font-semibold">New password</Label>
                <Input type="password" value={forgotNewPass} onChange={(e) => setForgotNewPass(e.target.value)} className="mt-1 h-11" data-testid="forgot-new-password-input" />
                <div className="text-[11px] text-muted-foreground mt-1">Min 8 chars, with upper, lower, number and symbol.</div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider font-semibold">Confirm new password</Label>
                <Input type="password" value={forgotConfirmPass} onChange={(e) => setForgotConfirmPass(e.target.value)} className="mt-1 h-11" data-testid="forgot-confirm-password-input" />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setForgotStep("request")}>Back</Button>
                <Button onClick={submitReset} disabled={forgotBusy} data-testid="forgot-submit-btn" className="bg-emerald-600 text-white hover:bg-emerald-700">
                  {forgotBusy ? "Resetting…" : "Reset password"}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Must-change password screen (first login) */}
      <Dialog open={!!mustChange} onOpenChange={(o) => !o && setMustChange(null)}>
        <DialogContent className="max-w-md" data-testid="must-change-password-dialog" onEscapeKeyDown={(e) => e.preventDefault()} onPointerDownOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-bold flex items-center gap-2">
              <Lock size={20} className="text-emerald-600" weight="fill" /> Set your new password
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground leading-relaxed">
              For your security, you must set a new password before continuing. We&apos;ll email a 6-digit verification code to <b>{mustChange?.user?.email}</b>.
            </p>
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold">New password</Label>
              <Input type="password" value={mcNew} onChange={(e) => setMcNew(e.target.value)} className="mt-1 h-11" data-testid="mc-new-password-input" />
              <div className="text-[11px] text-muted-foreground mt-1">Min 8 chars, with upper, lower, number and symbol.</div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider font-semibold">Confirm new password</Label>
              <Input type="password" value={mcConfirm} onChange={(e) => setMcConfirm(e.target.value)} className="mt-1 h-11" data-testid="mc-confirm-password-input" />
            </div>
            {mcOtpSent && (
              <div>
                <Label className="text-xs uppercase tracking-wider font-semibold">6-digit verification code</Label>
                <Input value={mcOtp} onChange={(e) => setMcOtp(e.target.value)} maxLength={6} className="mt-1 h-11 font-mono tracking-widest text-lg" data-testid="mc-otp-input" />
              </div>
            )}
            <DialogFooter>
              {!mcOtpSent ? (
                <Button onClick={requestMcOtp} disabled={mcBusy || !mcNew || !mcConfirm} data-testid="mc-send-otp-btn" className="w-full bg-slate-900 text-white hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-900">
                  {mcBusy ? "Sending code…" : "Send verification code"}
                </Button>
              ) : (
                <Button onClick={submitMustChange} disabled={mcBusy} data-testid="mc-submit-btn" className="w-full bg-emerald-600 text-white hover:bg-emerald-700">
                  {mcBusy ? "Setting password…" : "Confirm & sign in"}
                </Button>
              )}
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// -------- Debounced typeahead for the investor firm dropdown --------
// Calls GET /api/investor-firms/search?q=... only after 2+ chars, with a 300ms
// debounce. The endpoint is rate-limited and returns only {id, name} so we
// never leak the full investor directory or firm domains to the client.
// If the user's firm isn't in the directory they can simply type their firm
// name and submit — the value becomes a free-text `firm_name` that our
// compliance team reviews at approval time.
function FirmTypeahead({ value, onSelect, onFreeText }) {
  const [q, setQ] = useState(value?.name || "");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [highlighted, setHighlighted] = useState(0);
  const [rateLimited, setRateLimited] = useState(false);

  useEffect(() => {
    // If a firm was picked from the list (has id), don't overwrite the input
    if (value?.id) return;
    const term = q.trim();
    if (term.length < 2) {
      setResults([]);
      setLoading(false);
      return;
    }
    // Propagate the free-text value so parent stays in sync while typing
    if (onFreeText) onFreeText(term);
    setLoading(true);
    const handle = setTimeout(async () => {
      try {
        const r = await api.get("/investor-firms/search", { params: { q: term } });
        setResults(Array.isArray(r.data) ? r.data : []);
        setHighlighted(0);
        setRateLimited(false);
      } catch (err) {
        if (err?.response?.status === 429) {
          setRateLimited(true);
        }
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 350);
    return () => clearTimeout(handle);
  }, [q, value?.id, onFreeText]);

  const pick = (f) => {
    onSelect(f);
    setQ(f.name);
    setResults([]);
    setOpen(false);
  };

  const clear = () => {
    onSelect(null);
    setQ("");
    setResults([]);
    setOpen(true);
  };

  const onKey = (e) => {
    if (!open) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setHighlighted((h) => Math.min(results.length - 1, h + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHighlighted((h) => Math.max(0, h - 1)); }
    else if (e.key === "Enter" && results[highlighted]) { e.preventDefault(); pick(results[highlighted]); }
    else if (e.key === "Escape") { setOpen(false); }
  };

  // Selected chip mode — firm picked from directory
  if (value?.id && value?.name) {
    return (
      <div className="flex items-center gap-2 h-11 rounded-md border border-emerald-500/40 bg-emerald-50/50 dark:bg-emerald-950/20 px-3" data-testid="firm-selected-pill">
        <Buildings size={16} className="text-emerald-600 shrink-0" />
        <div className="flex-1 min-w-0 truncate font-medium text-sm">{value.name}</div>
        <span className="text-[9px] uppercase tracking-wider font-bold text-emerald-700 dark:text-emerald-400">Verified</span>
        <button
          type="button"
          onClick={clear}
          className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground hover:text-red-600 transition-colors"
          data-testid="firm-change-btn"
        >
          Change
        </button>
      </div>
    );
  }

  const showEmpty = !loading && q.trim().length >= 2 && results.length === 0 && !rateLimited;

  return (
    <div className="relative">
      <div className="relative">
        <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
        <Input
          value={q}
          onChange={(e) => {
            const v = e.target.value;
            setQ(v);
            setOpen(true);
            if (onFreeText) onFreeText(v.trim());
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          onKeyDown={onKey}
          placeholder="Start typing your firm name…"
          className="h-11 pl-9 pr-9"
          data-testid="register-firm-search-input"
          autoComplete="off"
          maxLength={120}
        />
        {loading && <CircleNotch size={15} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground animate-spin" />}
        {!loading && q && (
          <button
            type="button"
            onMouseDown={(e) => { e.preventDefault(); setQ(""); if (onFreeText) onFreeText(""); }}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            aria-label="Clear firm name"
          >
            <X size={13} />
          </button>
        )}
      </div>

      {open && (results.length > 0 || rateLimited || (q.trim().length >= 2 && showEmpty)) && (
        <div
          className="absolute z-30 mt-1 w-full rounded-xl border border-border bg-popover shadow-xl overflow-hidden max-h-72 overflow-y-auto"
          data-testid="firm-search-dropdown"
        >
          {rateLimited ? (
            <div className="px-3 py-3 text-xs text-amber-700 dark:text-amber-400" data-testid="firm-search-rate-limited">
              Too many searches — please wait a moment. You can also just keep typing your firm name and submit.
            </div>
          ) : showEmpty ? (
            <div className="px-3 py-3 text-xs" data-testid="firm-search-no-results">
              <div className="text-muted-foreground">No approved firms match &quot;{q}&quot;.</div>
              <div className="mt-1.5 text-emerald-700 dark:text-emerald-400 font-semibold">✓ We&apos;ll use &quot;{q}&quot; — our compliance team will review your application.</div>
            </div>
          ) : (
            <ul>
              {results.map((f, i) => (
                <li key={f.id}>
                  <button
                    type="button"
                    onMouseDown={(e) => { e.preventDefault(); pick(f); }}
                    onMouseEnter={() => setHighlighted(i)}
                    data-testid={`firm-search-result-${i}`}
                    className={`w-full text-left px-3 py-2.5 flex items-center gap-2.5 transition-colors ${i === highlighted ? "bg-emerald-50 dark:bg-emerald-950/30" : "hover:bg-secondary"}`}
                  >
                    <Buildings size={15} className="text-emerald-600 shrink-0" />
                    <span className="text-sm font-medium flex-1 truncate">{f.name}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function IndustriesPicker({ options, value, onChange }) {
  const selected = Array.isArray(value) ? value : [];
  const toggle = (ind) => {
    onChange(selected.includes(ind) ? selected.filter((x) => x !== ind) : [...selected, ind]);
  };
  if (!options || options.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-border px-3 py-3 text-xs text-muted-foreground" data-testid="register-interests-empty">
        Loading industries…
      </div>
    );
  }
  return (
    <div className="rounded-xl border border-border bg-secondary/30 p-3" data-testid="register-interests-picker">
      <div className="flex flex-wrap gap-1.5">
        {options.map((ind) => {
          const on = selected.includes(ind);
          return (
            <button
              key={ind}
              type="button"
              onClick={() => toggle(ind)}
              data-testid={`register-interest-${ind}`}
              className={
                "inline-flex items-center h-8 px-3 rounded-full text-xs font-semibold transition-all border " +
                (on
                  ? "bg-emerald-600 text-white border-emerald-600 hover:bg-emerald-700"
                  : "bg-background text-foreground border-border hover:border-emerald-500 hover:text-emerald-700 dark:hover:text-emerald-400")
              }
            >
              {on && <CheckCircle size={12} weight="fill" className="mr-1" />}
              {ind}
            </button>
          );
        })}
      </div>
      <div className="mt-2 text-[10.5px] text-muted-foreground">
        {selected.length === 0 ? "No industries selected — you won't receive new-listing emails" : `${selected.length} selected · you'll be alerted when new companies are added in ${selected.length === 1 ? "this area" : "these areas"}`}
      </div>
    </div>
  );
}
